import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Search,
  Plus,
  Trash2,
  Eye,
  Printer,
  FileText,
  Calendar,
  Package,
  Filter,
  ChevronDown,
} from "lucide-react";

type Cod = {
  id: number;
  cod_number: string;
  date_received: string;
  received_from: string;
  vanguard_work_order: string | null;
  destruction_location: string | null;
  material_count: number;
  status: string;
  client_tag: string | null;
  created_at: string;
};

type CodStats = {
  total: number;
  this_week: number;
  total_materials: number;
};

function formatDate(dateString: string | null) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    completed: "bg-forest-100 text-forest-700 dark:bg-forest-900/30 dark:text-forest-400",
    pending: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold capitalize ${
        styles[status] || "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
      }`}
    >
      {status}
    </span>
  );
}

function ClientBadge({ tag }: { tag: string | null }) {
  if (!tag) return <span className="text-muted-foreground">-</span>;
  const styles: Record<string, string> = {
    vanguard: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    willcox: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    "3lag": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
        styles[tag] || "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
      }`}
    >
      {tag}
    </span>
  );
}

export default function CodList() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [cods, setCods] = useState<Cod[]>([]);
  const [stats, setStats] = useState<CodStats>({ total: 0, this_week: 0, total_materials: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [clientTagFilter, setClientTagFilter] = useState("all");

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);

  // Dropdown menu
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const loadCods = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (dateFilter) params.set("dateFilter", dateFilter);
      if (clientTagFilter !== "all") params.set("client_tag", clientTagFilter);

      const res = await apiFetch(`/api/ops/cods?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load CODs (${res.status})`);

      const data = await res.json();
      setCods(data.cods || []);
      setStats(data.stats || { total: 0, this_week: 0, total_materials: 0 });
    } catch (err: any) {
      setError(err?.message || "Failed to load CODs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCods();
  }, [token, statusFilter, dateFilter, clientTagFilter]);

  // Close menu on click outside
  useEffect(() => {
    if (openMenuId === null) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [openMenuId]);

  const filteredCods = useMemo(() => {
    if (!search.trim()) return cods;
    const q = search.trim().toLowerCase();
    return cods.filter((cod) => {
      const haystack = [
        cod.cod_number,
        cod.received_from,
        cod.vanguard_work_order || "",
        cod.destruction_location || "",
        cod.client_tag || "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [cods, search]);

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredCods.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCods.map((c) => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} COD(s)? This cannot be undone.`)) return;

    setDeleting(true);
    try {
      const res = await apiFetch("/api/ops/cods/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      if (!res.ok) throw new Error("Failed to delete CODs");
      setSelectedIds(new Set());
      await loadCods();
    } catch (err: any) {
      setError(err?.message || "Failed to delete CODs");
    } finally {
      setDeleting(false);
    }
  };

  const handleSingleDelete = async (id: number) => {
    if (!confirm("Delete this COD? This cannot be undone.")) return;
    try {
      const res = await apiFetch("/api/ops/cods/delete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: [id] }),
      });
      if (!res.ok) throw new Error("Failed to delete COD");
      await loadCods();
    } catch (err: any) {
      setError(err?.message || "Failed to delete COD");
    }
  };

  const handlePrintPdf = (id: number) => {
    const url = `/api/ops/cods/${id}/pdf?token=${token}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4 max-w-7xl">
      {/* Header + stats inline */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-4 flex-wrap">
          <h1 className="heading-display text-2xl text-foreground">Certificates of Destruction</h1>
          {!loading && (
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <FileText size={14} className="text-forest-500" />
                <strong className="text-foreground tabular-nums">{stats.total}</strong> total
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Calendar size={14} className="text-blue-500" />
                <strong className="text-foreground tabular-nums">{stats.this_week}</strong> this week
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Package size={14} className="text-amber-500" />
                <strong className="text-foreground tabular-nums">{stats.total_materials}</strong> materials
              </span>
            </div>
          )}
        </div>
        <Link
          to="/app/ops/cods/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
        >
          <Plus size={16} />
          New COD
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-3 space-y-2.5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
          <label className="lg:col-span-2 flex items-center gap-2 px-3 bg-background border border-border rounded-lg">
            <Search size={15} className="text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search CODs..."
              className="w-full py-2 text-sm bg-transparent outline-none"
            />
          </label>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
          </select>

          <select
            value={clientTagFilter}
            onChange={(e) => setClientTagFilter(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          >
            <option value="all">All Clients</option>
            <option value="vanguard">Vanguard</option>
            <option value="willcox">Willcox</option>
            <option value="3lag">3LAG</option>
          </select>

          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm"
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Filter size={12} />
            {filteredCods.length} records
          </div>

          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              <Trash2 size={12} />
              {deleting ? "Deleting..." : `Delete ${selectedIds.size} selected`}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading CODs...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-600 dark:text-red-400">{error}</div>
        ) : filteredCods.length === 0 ? (
          <div className="p-16 text-center">
            <FileText size={36} className="mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No CODs found</p>
            <Link
              to="/app/ops/cods/new"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              <Plus size={14} />
              Create your first COD
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filteredCods.length && filteredCods.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-border"
                    />
                  </th>
                  <th className="px-3 py-2">COD #</th>
                  <th className="px-3 py-2">Date Received</th>
                  <th className="px-3 py-2">Received From</th>
                  <th className="px-3 py-2">Vanguard WO #</th>
                  <th className="px-3 py-2">Location</th>
                  <th className="px-3 py-2">Materials</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2 w-20 text-right" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredCods.map((cod) => (
                  <tr
                    key={cod.id}
                    className="border-b border-border/60 last:border-0 hover:bg-secondary/30 transition-colors"
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(cod.id)}
                        onChange={() => toggleSelect(cod.id)}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => navigate(`/app/ops/cods/${cod.id}`)}
                        className="font-semibold text-forest-600 dark:text-forest-400 hover:underline"
                      >
                        {cod.cod_number}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                      {formatDate(cod.date_received)}
                    </td>
                    <td className="px-3 py-2 text-foreground">{cod.received_from}</td>
                    <td className="px-3 py-2 text-muted-foreground font-mono text-xs">
                      {cod.vanguard_work_order || "-"}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{cod.destruction_location || "-"}</td>
                    <td className="px-3 py-2 text-foreground tabular-nums">{cod.material_count}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={cod.status} />
                    </td>
                    <td className="px-3 py-2">
                      <ClientBadge tag={cod.client_tag} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === cod.id ? null : cod.id);
                          }}
                          className="p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <ChevronDown size={14} />
                        </button>
                        {openMenuId === cod.id && (
                          <div className="absolute right-0 top-full mt-1 z-20 w-40 bg-card border border-border rounded-lg shadow-lg py-1">
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                navigate(`/app/ops/cods/${cod.id}`);
                              }}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors"
                            >
                              <Eye size={14} />
                              View
                            </button>
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                handlePrintPdf(cod.id);
                              }}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors"
                            >
                              <Printer size={14} />
                              Print PDF
                            </button>
                            <button
                              onClick={() => {
                                setOpenMenuId(null);
                                handleSingleDelete(cod.id);
                              }}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
